import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  CircularProgress,
  Snackbar,
  makeStyles
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { Refresh } from '@material-ui/icons';
import { useAuth } from '../context/ApiKeyContext';
import { useTranslation } from '../hooks/useTranslation';
import JobHeader from './JobHeader';
import JobTable from './JobTable';
import JobFilters from './JobFilters';
import JobPagination from './JobPagination';

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: '100vh',
    backgroundColor: 'var(--ikaro-gray-light)',
  },
  container: {
    padding: theme.spacing(3),
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '200px',
  },
  paper: {
    backgroundColor: 'var(--ikaro-text-white)',
    borderRadius: theme.spacing(1),
    boxShadow: 'var(--ikaro-shadow-light)',
    overflow: 'hidden',
  },
  refreshButton: {
    borderColor: 'var(--ikaro-orange-primary)',
    color: 'var(--ikaro-orange-primary)',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: 'var(--ikaro-orange-light)',
    },
    '&:disabled': {
      borderColor: 'var(--ikaro-gray-medium)',
      color: 'var(--ikaro-gray-dark)',
    },
  },
}));

const JobManager = ({
  jobType = 'processamento',
  title = 'Processamentos',
  subtitle = 'Gerencie e monitore todos os processamentos',
  apiEndpoint = '/job/get_jobs_list',
  createJobPath = '/novo-processamento',
  columns = [],
  filterFields = [],
  customActions = [],
  customHeaderActions = [],
  onNewJob = null,
  onJobClick = null,
  onJobAction = null,
  customFilters = {},
  defaultFilters = {},
  cellId = null, // Mudança: removido valor padrão fixo
  code = null,
}) => {
  const classes = useStyles();
  const { token, cell } = useAuth(); // Mudança: importando cell do contexto
  const { t } = useTranslation('jobs');
  const { t: tCommon } = useTranslation('common');
  
  // Determinar qual cell_id usar: prop fornecida ou célula selecionada no contexto
  const effectiveCellId = cellId || cell?.value;
  
  // Função para calcular os valores padrão das datas
  const getDefaultDateFilters = () => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);
    
    return {
      date_start: thirtyDaysAgo.toISOString().split('T')[0],
      date_end: today.toISOString().split('T')[0],
    };
  };

  // Combinar filtros padrão com valores padrão de data
  const combinedDefaultFilters = {
    ...getDefaultDateFilters(),
    ...defaultFilters,
  };
  
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(combinedDefaultFilters);
  const [showFilters, setShowFilters] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 5,
    total: 0,
    totalPages: 0,
  });
  const [sortConfig, setSortConfig] = useState({
    sortBy: 'insertion_timestamp',
    sortOrder: 'desc',
  });

  // URL base da API
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const fetchJobs = useCallback(async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);
    
    try {
      const queryParams = new URLSearchParams({
        page: pagination.page,
        page_size: pagination.pageSize,
        sort_by: sortConfig.sortBy,
        sort_order: sortConfig.sortOrder,
        source_type: 'user',
        ...customFilters,
      });

      // Adicionar filtros opcionais
      if (effectiveCellId) queryParams.append('cell_id', effectiveCellId); // Mudança: usando effectiveCellId
      if (code) queryParams.append('code', code);
      
      // Adicionar filtros personalizados
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value.trim() !== '') {
          queryParams.append(key, value);
        }
      });

      const url = `${API_BASE_URL}${apiEndpoint}?${queryParams}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Erro na requisição: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      setJobs(data.jobs || []);
      setPagination(prev => ({
        ...prev,
        total: data.pagination?.total_count || 0,
        totalPages: data.pagination?.total_pages || 0,
      }));
      
    } catch (err) {
      console.error('Erro ao carregar jobs:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [pagination.page, pagination.pageSize, sortConfig.sortBy, sortConfig.sortOrder, customFilters, effectiveCellId, code, filters, apiEndpoint, token, API_BASE_URL]); // Mudança: usando effectiveCellId nas dependências

  // Carrega os jobs quando o componente é montado ou filtros/paginação mudam
  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  // Reseta a paginação para página 1 quando a célula muda
  useEffect(() => {
    setPagination(prev => ({ ...prev, page: 1 }));
  }, [effectiveCellId]);

  const handleRefresh = () => {
    fetchJobs(true);
  };

  const handleFilterChange = (filterKey, value) => {
    setFilters(prev => ({
      ...prev,
      [filterKey]: value,
    }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleClearFilters = () => {
    setFilters(combinedDefaultFilters);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({
      ...prev,
      page: newPage,
    }));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination(prev => ({
      ...prev,
      pageSize: newPageSize,
      page: 1,
    }));
  };

  const handleSort = (field) => {
    setSortConfig(prev => ({
      sortBy: field,
      sortOrder: prev.sortBy === field && prev.sortOrder === 'asc' ? 'desc' : 'asc',
    }));
  };

  const handleNewJob = () => {
    if (onNewJob) {
      onNewJob();
    } else if (createJobPath) {
      window.location.href = createJobPath;
    }
  };

  const handleJobClick = (job) => {
    if (onJobClick) {
      onJobClick(job);
    }
  };

  const handleJobAction = (action, job) => {
    if (onJobAction) {
      onJobAction(action, job);
    }
  };

  const handleCloseError = () => {
    setError(null);
  };


  // Criar as ações do header incluindo ações customizadas e refresh
  const headerActions = [
    ...customHeaderActions,
    {
      label: tCommon('refresh'),
      icon: <Refresh />,
      onClick: handleRefresh,
      variant: 'outlined',
      className: classes.refreshButton,
      disabled: refreshing,
    },
  ];

  return (
    <Box className={classes.root}>
      <JobHeader
        title={title}
        subtitle={subtitle}
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters(!showFilters)}
        onNewJob={handleNewJob}
        customActions={headerActions}
      />

      {showFilters && (
        <JobFilters
          filters={filters}
          filterFields={filterFields}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
        />
      )}

      <Box className={classes.container}>
        <Paper className={classes.paper}>
          {loading ? (
            <Box className={classes.loading}>
              <CircularProgress />
              <Typography variant="body1" style={{ marginLeft: 16 }}>
                {t('table.loadingJobs')}
              </Typography>
            </Box>
          ) : (
            <>
              <JobTable
                jobs={jobs}
                columns={columns}
                customActions={customActions}
                sortConfig={sortConfig}
                onSort={handleSort}
                onJobClick={handleJobClick}
                onJobAction={handleJobAction}
              />

              <JobPagination
                page={pagination.page}
                pageSize={pagination.pageSize}
                total={pagination.total}
                totalPages={pagination.totalPages}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            </>
          )}
        </Paper>
      </Box>

      {/* Snackbar para erros */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseError} severity="error">
          {error}
        </Alert>
      </Snackbar>

    </Box>
  );
};

export default JobManager; 